import type { RefObject } from 'react'

import { useEffect, useRef, useState } from 'react'

interface InViewOptions {
    once?: boolean
    rootMargin?: string
}

/**
 * Tracks whether an element is inside the viewport. Starts false on the
 * server and flips after mount, so it never causes a hydration mismatch.
 */
export const useInView = <T extends Element>({ once = false, rootMargin = '0px' }: InViewOptions = {}): [RefObject<T | null>, boolean] => {
    const ref = useRef<T | null>(null)
    const [inView, setInView] = useState(false)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true)

                if (once) observer.disconnect()
            }
            else if (!once) {
                setInView(false)
            }
        }, { rootMargin })

        observer.observe(element)

        return () => observer.disconnect()
    }, [once, rootMargin])

    return [ref, inView]
}
